import { CrudPage } from '../../components/CrudPage';
import { type MaterialDto, MaterialControllerService } from '../../api';
import MaterialFormDialog from '../forms/MaterialFormDialog';
import { useTranslation } from 'react-i18next';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';

// Type with required id for the CrudPage
type MaterialWithId = MaterialDto & { id: string };

const RawMaterialsPage = () => {
  const { t } = useTranslation();

  const formatUnitType = (data: any) => {
    const value = data.baseUnitType;
    switch (value) {
      case 'PIECE': return t('form.unitType.piece');
      case 'MASS': return t('form.unitType.mass');
      case 'VOLUME': return t('form.unitType.volume');
      case 'LENGTH': return t('form.unitType.length');
      case 'AREA': return t('form.unitType.area');
      default: return value;
    }
  };

  const formatBaseUnit = (data: any) => {
    const value = data.baseUnit;
    if (!value) return '';
    
    // Map backend enum values to translation keys
    const unitMap: { [key: string]: string } = {
      'COUNT': 'piece',
      'GRAM': 'gram',
      'KILOGRAM': 'kilogram',
      'MILLIGRAM': 'milligram',
      'TON': 'ton',
      'LITER': 'liter',
      'MILLILITER': 'milliliter',
      'CUBIC_METER': 'cubicMeter',
      'METER': 'meter',
      'CENTIMETER': 'centimeter',
      'MILLIMETER': 'millimeter',
      'KILOMETER': 'kilometer',
      'SQUARE_METER': 'squareMeter',
      'SQUARE_CENTIMETER': 'squareCentimeter',
      'SQUARE_MILLIMETER': 'squareMillimeter'
    };
    
    const translationKey = unitMap[value] || value.toLowerCase();
    return t(`form.baseUnit.${translationKey}`);
  };

  // Checkbox template for global field
  const GlobalCheckboxTemplate = (data: any) => {
    return (
      <div className="flex justify-center">
        <CheckBoxComponent
          checked={data.global}
          disabled={true}
          cssClass="e-success"
        />
      </div>
    );
  };

  const gridColumns = [
    { field: 'name', headerKey: 'grid.name', width: 150 },
    { field: 'description', headerKey: 'grid.description', width: 200 },
    { field: 'baseUnitType', headerKey: 'form.baseUnitType', width: 120, render: formatUnitType },
    { field: 'baseUnit', headerKey: 'form.baseUnit', width: 120, render: formatBaseUnit },
    { field: 'global', headerKey: 'form.global', width: 100, render: GlobalCheckboxTemplate }
  ];

  return (
    <CrudPage<MaterialWithId>
      titleKey="routes.rawMaterials"
      gridMeta={gridColumns}
      fetchData={async () => {
        const materials = await MaterialControllerService.findAll();
        const rawMaterials = materials.filter(material => material.materialType === 'RAW_MATERIAL');
        return rawMaterials.map(material => ({
          ...material,
          id: material.uuid || ''
        }));
      }}
      saveData={async (data: MaterialWithId) => {
        try {
          // Ensure materialType is set to RAW_MATERIAL
          const rawMaterialData = { ...data, materialType: 'RAW_MATERIAL' as const };

          if (rawMaterialData.id) {
            // Update existing raw material
            await MaterialControllerService.update(rawMaterialData.id, rawMaterialData);
          } else {
            // Create new raw material
            await MaterialControllerService.create(rawMaterialData);
          }
        } catch (error: any) {
          console.error('RawMaterialsPage: Error saving raw material:', error);
          console.error('RawMaterialsPage: Save error details:', {
            message: error?.message,
            status: error?.status,
            response: error?.response,
            body: error?.body
          });
          throw error;
        }
      }}
      deleteData={MaterialControllerService.delete}
      FormComponent={(props) => <MaterialFormDialog {...props} materialType="RAW_MATERIAL" />}
      entityName="material"
    />
  );
};

export default RawMaterialsPage;
